import { Dispatch } from '@reduxjs/toolkit'
import { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import { SessionError } from '@typesApp/AppEvent'
import { useEnvironment } from '@hooks/useEnvironment'
import { claimRecordsApi } from '@redux/queries/claimRecords'
import { configApi } from '@redux/queries/config'
import { insurersApi } from '@redux/queries/insurers'
import { resetClaimStatusInitialState } from '@redux/slices/claimStatusSlice'
import { sessionExpired } from '@redux/slices/connectorSlice'
import { resetCtaInitialState } from '@redux/slices/ctaSlice'
import { resetInsuranceMemberState } from '@redux/slices/insuranceMemberSlice'
import { closeApp } from '@redux/slices/workflowSlice'
import { isSessionExpiredError } from '@utils/errorUtil'
import { logger } from '@utils/logger'
import { setLoginRedirectParam } from '@utils/navigationUtil'

export const useSessionErrorEventHandler = (): ((error: SessionError | undefined) => void) => {
  const dispatch = useDispatch()
  const [isInProgress, setIsInProgress] = useState(false)
  const { basePath, signInPath } = useEnvironment()

  return useCallback(
    (error: SessionError | undefined) => {
      if (isInProgress) return

      setIsInProgress(true)

      try {
        if (isSessionExpiredError(error)) {
          resetStateOnCommerceSessionExpiry(dispatch)

          // to allow commerce app to redirect back on next sign in
          setLoginRedirectParam({ basePath, signInPath })
        }
      } catch (error) {
        logger.warn('Error on session error event handler', error)
      } finally {
        setIsInProgress(false)
      }
    },
    [dispatch, isInProgress, basePath, signInPath]
  )
}

const resetStateOnCommerceSessionExpiry = (dispatch: Dispatch) => {
  dispatch(claimRecordsApi.util.resetApiState())
  dispatch(configApi.util.resetApiState())
  dispatch(insurersApi.util.resetApiState())

  dispatch(resetCtaInitialState())
  dispatch(resetInsuranceMemberState())
  dispatch(resetClaimStatusInitialState())

  dispatch(sessionExpired())
  dispatch(closeApp())
}
